/**
 * Reddit Pixel tracking utilities
 * https://advertising.reddithelp.com/en/categories/measurement/reddit-pixel-events
 */

// Extend window interface to include rdt function
declare global {
  interface Window {
    rdt?: (command: string, eventName: string, eventData?: Record<string, any>) => void;
  }
}

/**
 * Generate a unique conversion ID for deduplication
 * Format: timestamp-randomString
 */
const generateConversionId = (): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 11);
  return `${timestamp}-${randomStr}`;
};

/**
 * Track a Reddit Pixel event with automatic conversion ID generation
 * @param eventName - The name of the event to track
 * @param eventData - Optional data to send with the event
 * @param conversionId - Optional custom conversion ID (auto-generated if not provided)
 */
export const trackRedditEvent = (
  eventName: 'SignUp' | 'Lead' | 'AddToCart' | 'Purchase' | 'Custom',
  eventData?: Record<string, any>,
  conversionId?: string
) => {
  if (typeof window !== 'undefined' && window.rdt) {
    try {
      const finalConversionId = conversionId || generateConversionId();
      window.rdt('track', eventName, {
        conversionId: finalConversionId,
        ...eventData
      });
    } catch (error) {
      console.error(`[Reddit Pixel] Failed to track event: ${eventName}`, error);
    }
  }
};

/**
 * Track user signup
 * @param userId - The user's unique ID
 */
export const trackSignUpReddit = (userId: string) => {
  trackRedditEvent('SignUp', {}, `signup-${userId}`);
};

/**
 * Track profile completion (lead conversion)
 * @param userId - The user's unique ID
 */
export const trackProfileCompleteReddit = (userId: string) => {
  trackRedditEvent('Lead', {}, `profile-complete-${userId}`);
};

/**
 * Track meal plan generation (primary conversion)
 * @param userId - The user's unique ID
 * @param planId - The ID of the generated meal plan
 * @param recipeCount - Number of recipes in the plan
 */
export const trackMealPlanGenerationReddit = (
  userId: string,
  planId: string,
  recipeCount: number
) => {
  trackRedditEvent('Custom', {
    customEventName: 'MealPlanGenerated',
    planId,
    recipeCount
  }, `mealplan-${userId}-${planId}`);
};

/**
 * Track subscription purchase (actual revenue event)
 * @param userId - The user's unique ID
 * @param subscriptionId - The Stripe subscription ID
 * @param value - The subscription amount in USD
 */
export const trackSubscriptionReddit = (
  userId: string,
  subscriptionId: string,
  value: number
) => {
  trackRedditEvent('Purchase', {
    transactionId: subscriptionId,
    value,
    currency: 'USD',
    itemCount: 1
  }, `subscription-${userId}-${subscriptionId}`);
};
